import { useEffect, useRef, useState } from 'react'
import { createLevelMeter } from '@/lib/voiceCapture'

/** Acima disso conta como "falando". Fala normal passa de 0,05 com folga. */
const SPEAKING_THRESHOLD = 0.045
/**
 * Tempo que o indicador fica aceso depois do ultimo pico. Sem essa margem ele
 * pisca nas pausas entre palavras, que e pior do que nao ter indicador.
 */
const HOLD_MS = 320
const POLL_MS = 100

export interface VoiceSource {
    /** Quem esta falando — id do participante. */
    id: string
    stream: MediaStream
}

/**
 * Diz quem esta falando agora.
 *
 * Mede o audio que este navegador ja recebe, entao nao circula mensagem
 * nenhuma pra isso: cada cliente chega na sua propria conclusao, e ninguem
 * consegue mentir que esta falando.
 */
export function useVoiceLevels(sources: VoiceSource[]): Set<string> {
    const [speaking, setSpeaking] = useState<Set<string>>(new Set())
    const contextRef = useRef<AudioContext | null>(null)

    // Chave estavel: o efeito so refaz os medidores quando as fontes mudam de
    // verdade, nao a cada render da lista.
    const signature = sources.map((source) => `${source.id}:${source.stream.id}`).join('|')

    useEffect(() => {
        if (sources.length === 0) {
            setSpeaking((current) => (current.size === 0 ? current : new Set()))
            return
        }

        // Um contexto para todos os medidores: cada AudioContext custa uma
        // thread de audio, e abrir um por pessoa derrubaria maquina fraca.
        const context = contextRef.current ?? new AudioContext()
        contextRef.current = context

        // Suspenso, o analyser le zero pra sempre: ninguem apareceria falando e
        // o anel verde simplesmente nunca acenderia. Tentar aqui cobre o caso
        // comum; o gesto abaixo cobre o resto.
        if (context.state === 'suspended') void context.resume().catch(() => undefined)

        const meters = sources.map((source) => ({
            id: source.id,
            meter: createLevelMeter(source.stream, context)
        }))
        const lastLoud = new Map<string, number>()

        const timer = window.setInterval(() => {
            const now = Date.now()
            const next = new Set<string>()

            for (const { id, meter } of meters) {
                if (meter.read() >= SPEAKING_THRESHOLD) lastLoud.set(id, now)
                if (now - (lastLoud.get(id) ?? 0) <= HOLD_MS) next.add(id)
            }

            setSpeaking((current) => (sameSet(current, next) ? current : next))
        }, POLL_MS)

        return () => {
            window.clearInterval(timer)
            for (const { meter } of meters) meter.stop()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature])

    /**
     * Ultimo recurso: o primeiro gesto da pessoa destrava o contexto.
     *
     * A politica de autoplay so libera audio depois de uma interacao, e um
     * contexto criado antes dela fica suspenso — sem isto, quem abrisse a sala
     * por um link e nao clicasse em nada antes de alguem falar nunca veria o
     * indicador acender.
     */
    useEffect(() => {
        const destravar = () => {
            const context = contextRef.current
            if (context?.state === 'suspended') void context.resume().catch(() => undefined)
        }
        window.addEventListener('pointerdown', destravar)
        window.addEventListener('keydown', destravar)
        return () => {
            window.removeEventListener('pointerdown', destravar)
            window.removeEventListener('keydown', destravar)
        }
    }, [])

    // O contexto vive enquanto o componente viver: recriar a cada mudanca de
    // fonte custaria caro e o navegador limita quantos existem ao mesmo tempo.
    useEffect(() => () => {
        void contextRef.current?.close()
        contextRef.current = null
    }, [])

    return speaking
}

function sameSet(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) return false
    for (const value of a) if (!b.has(value)) return false
    return true
}
